import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'

import { formatDate } from '~/utils'

const ChannelIconImage = styled('img')({
    height: 20,
    width: 20,
    mr: 1,
})

type ChannelProductDiscountSummary = {
    shop: {
        id: number
        shop_name: string
        channel: {
            id: string
            channel_square_icon_url: string
        }
    }
    data: {
        sku: string
        discount_price: number | string
    }[]
    has_more: boolean
}

type Props = {
    discount: {
        id: number
        name: string
        start_date?: string
        end_date?: string
        channel_product_discounts: ChannelProductDiscountSummary[]
    }
}

export default function DiscountSummary({ discount }: Props) {
    return (
        <Stack
            sx={{
                border: (theme) => `1px solid ${theme.palette.grey[400]}`,
                borderRadius: 1,
                py: 2,
                px: 3,
                gap: 1.5,
                minWidth: 450,
            }}
        >
            <Box sx={{ minWidth: 300 }}>
                <Typography variant="h6">{discount.name}</Typography>
                {discount.start_date && discount.end_date && (
                    <Typography variant="subtitle1" color="grey.500">
                        {formatDate(discount.start_date, { format: 'D MMM' })}
                        {' - '}
                        {formatDate(discount.end_date, { format: 'D MMM' })}
                    </Typography>
                )}
            </Box>

            <Stack gap={1.5}>
                {discount.channel_product_discounts.map((cpd) => (
                    <Box key={cpd.shop.id} sx={{ display: 'flex', gap: 1 }}>
                        <ChannelIconImage
                            sx={{ flexShrink: 0 }}
                            src={cpd.shop.channel.channel_square_icon_url}
                        />

                        <Stack sx={{ flexGrow: 1 }}>
                            <Typography variant="body1">{cpd.shop.shop_name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: '100%' }}>
                                    {cpd.data.map((discount) => (
                                        <Box
                                            key={discount.sku}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography variant="body2" color="grey.500">
                                                {discount.sku}
                                            </Typography>
                                            <Typography variant="body2" color="grey.500">
                                                {discount.discount_price}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {cpd.has_more && <Box sx={{ color: 'grey.500' }}>...</Box>}
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Stack>
    )
}
